// Layer 3 (Transformation) — money per lost mill hour (a20-maintenance S2),
// extended by S4 with the Value lost tab's cuts: the KPI read, category,
// cumulative curve and event rows.
// Pure: no React, no engine mutations, no formatting beyond the compact display
// forms the glass needs.
//
// durable: lost production is priced at CapacityEngine's current-point EBITDA
// per operating hour. Upstream stoppages consume their seeded buffer first,
// grinding stops the mill directly, and downstream equipment outages remain in
// the event log without becoming lost mill production.
//
// `rows` is always the full YTD event log (`downtimeEvents()`), passed in by the
// caller — these functions never call it themselves, so the tab cuts the same
// log the Causes Pareto and the Reliability roll-up read without a second
// projection. Every share is unrounded here; the UI rounds. This file does not
// import Causes.transform.ts, which imports it (the reverse edge is a cycle).

import { riskAtConstraint } from '@/versions/v1/modules/risk/Scene.transform';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { formatCurrency } from '@/versions/v1/shared/money';
import { capacityAtReference } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { BM01_BEARING_STOP_HOURS } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { BM01_BEARING_SERIES_SHAPE } from '@/versions/v1/shared/bunker-hill-bearing-trend';
import { DOWNTIME_STATES } from '../maintenance/downtime.seed';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { ebitdaPerOperatingHour, stopValue } from './OperatingValue.transform';
export { ebitdaPerOperatingHour, stopValue };
import { SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import {
  BASIS_END,
  CAUSE_CATEGORIES,
  type CauseCategoryId,
  type DowntimeItemId,
} from '../maintenance/downtime.seed';
import { ITEM_SUBPROCESS, YTD_START } from '../maintenance/downtime-events.seed';
import {
  causeCategoryLabel,
  eventsByKind,
  type DowntimeEvent,
} from '../maintenance/DowntimeEvents.transform';

const CAPACITY_CURRENT_READ = capacityAtReference(CAPACITY_SEED);

/** True when the item's subprocess is the plant's bottleneck, read from the
 *  same `SUBPROCESSES` flag the risk register's budget impact reads. */
export function itemAtConstraint(itemId: DowntimeItemId): boolean {
  return riskAtConstraint({ subprocessId: ITEM_SUBPROCESS[itemId] });
}

function itemOnMillFeedPath(itemId: DowntimeItemId): boolean {
  const item = SUBPROCESSES.find((row) => row.id === ITEM_SUBPROCESS[itemId]);
  const constraint = SUBPROCESSES.find((row) => row.isBottleneck);
  return item !== undefined && constraint !== undefined && item.sequenceOrder <= constraint.sequenceOrder;
}

/** Shared one-off planned/avoided comparison for the BM01 bearing. */
export function bm01BearingStopComparison() {
  const plannedHours = BM01_BEARING_STOP_HOURS.plannedInspection;
  const avoidedFailureHours = BM01_BEARING_STOP_HOURS.avoidedFailure;
  return {
    plannedHours,
    plannedAmount: stopValue(plannedHours),
    avoidedFailureHours,
    avoidedFailureAmount: stopValue(avoidedFailureHours),
  } as const;
}

export type BM01BearingTrendFit = {
  state: 'fitted';
  currentValue: number;
  currentAt: SeriesTime;
  restartValue: number;
  restartAt: SeriesTime;
  slopeCPerDay: number;
  direction: 'rising' | 'falling' | 'steady';
  limitC: number;
  daysToLimit: number | null;
  limitAt: SeriesTime | null;
  limitDate: string | null;
};

/** Why the fit is absent. Probes read it; the glass never prints it — every
 *  surface renders the closed honest state `unmeasured` for the trend itself. */
export type BM01BearingTrendGap =
  | 'before-restart'
  | 'fewer-than-two-samples'
  | 'no-span'
  | 'missing-seed';

/** The honest state before the trend can be fitted (fix #782): a cursor before
 *  the WO-441 restart, too few post-settle samples, or no span between them.
 *  The Reading's spec limit and the last measured sample still ride along, so
 *  the limit comparison stands while the trend reads `unmeasured`. */
export type BM01BearingTrendUnmeasured = {
  state: 'unmeasured';
  reason: BM01BearingTrendGap;
  limitC: number | null;
  currentValue: number | null;
  currentAt: SeriesTime | null;
};

export type BM01BearingTrend = BM01BearingTrendFit | BM01BearingTrendUnmeasured;

/** Least-squares bearing rate over the post-settle operating samples, ending
 *  at the scenario cursor. The WO-441 interval supplies the restart boundary;
 *  the Reading supplies the spec limit.
 *
 *  Total by contract (#782): a cursor the trend cannot be fitted at returns an
 *  `unmeasured` read instead of throwing, so no render path throws — the same
 *  rule P13 r1 applied to the Ops supervisor landing. The three cursor states
 *  are pre-restart, fewer than two post-settle samples, and post-settle stamps
 *  with no span; a missing seed row is the fourth, and is a seed-integrity gap
 *  rather than a cursor state. */
export function bm01BearingTrend(state: SessionState): BM01BearingTrend {
  const trip = DOWNTIME_STATES.find((row) => row.workOrderId === BM01_BEARING_SERIES_SHAPE.trip.workOrderId);
  const reading = READINGS.find((row) => row.id === 'rd-bm-brg-temp');
  const specHi = reading?.spec?.hi;
  const series = seriesAt('rs-bm-brg-temp', state);
  const latest = series.latest;
  const unmeasured = (reason: BM01BearingTrendGap): BM01BearingTrendUnmeasured => ({
    state: 'unmeasured',
    reason,
    limitC: specHi ?? null,
    currentValue: latest?.value ?? null,
    currentAt: latest?.at ?? null,
  });

  if (!trip || specHi === undefined) return unmeasured('missing-seed');
  const restart = series.points.find((point) => point.at >= trip.end && point.at <= state.cursor && point.value !== null);
  if (!restart) return unmeasured('before-restart');
  const points = series.points.filter((point) =>
    point.value !== null && point.at >= BM01_BEARING_SERIES_SHAPE.settleEndAt && point.at <= state.cursor);
  if (points.length < 2) return unmeasured('fewer-than-two-samples');

  const xDays = points.map((point) => diffMinutes(point.at, BM01_BEARING_SERIES_SHAPE.settleEndAt) / (24 * 60));
  const yTemps = points.map((point) => point.value as number);
  const meanX = xDays.reduce((sum, value) => sum + value, 0) / xDays.length;
  const meanY = yTemps.reduce((sum, value) => sum + value, 0) / yTemps.length;
  const variance = xDays.reduce((sum, value) => sum + (value - meanX) ** 2, 0);
  const covariance = xDays.reduce((sum, value, index) => sum + (value - meanX) * (yTemps[index]! - meanY), 0);
  if (variance === 0) return unmeasured('no-span');
  const slopeCPerDay = covariance / variance;
  const direction = slopeCPerDay > 0 ? 'rising' : slopeCPerDay < 0 ? 'falling' : 'steady';
  const lastPoint = points.at(-1)!;
  const daysToLimit = slopeCPerDay > 0 ? Math.max(0, (specHi - lastPoint.value!) / slopeCPerDay) : null;
  const limitAt = daysToLimit === null ? null : addMinutes(state.cursor, daysToLimit * 24 * 60);

  return {
    state: 'fitted',
    currentValue: lastPoint.value!,
    currentAt: lastPoint.at,
    restartValue: restart.value!,
    restartAt: restart.at,
    slopeCPerDay,
    direction,
    limitC: specHi,
    daysToLimit,
    limitAt,
    limitDate: limitAt ? formatDayMonth(limitAt) : null,
  };
}

/** Shared wording for each BM01 surface, using only `bm01BearingTrend` output.
 *  An unfitted trend reads the closed honest state `unmeasured`. */
export function bm01BearingTrendPhrase(trend: BM01BearingTrend): string {
  if (trend.state !== 'fitted') return 'unmeasured';
  const rate = `${trend.direction} ${Math.abs(trend.slopeCPerDay).toFixed(1)} °C/day since ${formatDayMonth(trend.restartAt)} restart`;
  return trend.daysToLimit === null || trend.limitDate === null
    ? rate
    : `${rate} · ${trend.limitC} °C limit in ≈ ${trend.daysToLimit.toFixed(1)} d (${trend.limitDate})`;
}

/** Dollars lost to one stoppage. Planned downtime and downstream equipment
 *  failures remain events but do not reduce mill feed production. */
export function valueLostForEvent(event: DowntimeEvent): number {
  if (event.kind !== 'unplanned') return 0;
  return valueLostHoursForEvent(event) * ebitdaPerOperatingHour();
}

/** Lost mill production hours. Grinding outages stop feed directly; upstream
 *  inventory absorbs its buffer; downstream events remain equipment-only. */
export function valueLostHoursForEvent(event: DowntimeEvent): number {
  if (event.kind !== 'unplanned') return 0;
  if (!itemOnMillFeedPath(event.itemId)) return 0;
  return itemAtConstraint(event.itemId) ? event.hours : Math.max(0, event.hours - event.bufferHours);
}

/** Lost feed short tons at the current capacity operating rate. */
export function valueLostTonnesForEvent(event: DowntimeEvent): number {
  return valueLostHoursForEvent(event) * CAPACITY_CURRENT_READ.currentThroughputStph;
}

export function valueLostTotal(rows: readonly DowntimeEvent[]): number {
  return rows.reduce((total, row) => total + valueLostForEvent(row), 0);
}

// ---------------------------------------------------------------------------
// The tab's cuts (S4). Every one of them reads the same unplanned population,
// so the counts, hours and dollars reconcile across the strip, category bars,
// cumulative curve and event table by construction.
// ---------------------------------------------------------------------------

export type ValueLostKpis = {
  /** Σ valueLostForEvent over the unplanned rows. */
  total: number;
  eventCount: number;
  /** CapacityEngine current-point EBITDA per lost mill hour. */
  ratePerHour: number;
  largest: {
    id: string;
    itemId: DowntimeItemId;
    start: SeriesTime;
    hours: number;
    dollars: number;
  };
  /** Total dollars divided by the unplanned event count. */
  averagePerEvent: number;
  /** '01 Jan – 07 Jun' — `YTD_START` to the day before `BASIS_END`, which is the
   *  open day's start. Recomputed here rather than read from `causeKpis`,
   *  because Causes.transform.ts imports this file. */
  windowLabel: string;
};

export type CategoryValueRow = {
  causeCategory: CauseCategoryId;
  label: string;
  dollars: number;
  /** dollars ÷ total × 100 — unrounded. */
  sharePct: number;
  eventCount: number;
  hours: number;
};

/** The cumulative read: one point per unplanned event in start order, in
 *  thousands of dollars (the chart's y axis prints raw numbers). */
export type CumulativeRead = { categories: string[]; points: number[] };

/** The calendar grid a window's cumulative read stands on: one point per day
 *  boundary for a long window, one per hour boundary for a short one. */
export type CumulativeGrid = 'day' | 'hour';

/** A window's cumulative read plus the grid it was built on. */
export type CumulativeWindowRead = CumulativeRead & { grid: CumulativeGrid };

export type ValueLostEventRow = {
  id: string;
  itemId: DowntimeItemId;
  start: SeriesTime;
  causeLabel: string;
  subCause: string;
  hours: number;
  dollars: number;
};

/** Ranked dollars-descending, ties on label so the order is stable across runs. */
function byDollarsThenLabel<T extends { dollars: number; label: string }>(a: T, b: T): number {
  return b.dollars - a.dollars || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
}

export function valueLostKpis(rows: readonly DowntimeEvent[], windowLabel?: string): ValueLostKpis {
  const unplanned = eventsByKind(rows).unplanned;
  const total = valueLostTotal(unplanned);

  const events = valueLostEventRows(rows);
  const largest = events[0]!;

  return {
    total,
    eventCount: unplanned.length,
    ratePerHour: ebitdaPerOperatingHour(),
    averagePerEvent: unplanned.length > 0 ? total / unplanned.length : 0,
    largest: {
      id: largest.id,
      itemId: largest.itemId,
      start: largest.start,
      hours: largest.hours,
      dollars: largest.dollars,
    },
    windowLabel: windowLabel ?? `${formatDayMonth(YTD_START)} – ${formatDayMonth(addMinutes(BASIS_END, -24 * 60))}`,
  };
}

/** Every cause category priced over the unplanned rows — one that carries no
 *  stoppage this year (or one whose hours are fully buffered) renders at `$0`
 *  rather than vanishing, because an absent row would read as an absent cause. */
export function valueLostByCategory(rows: readonly DowntimeEvent[]): CategoryValueRow[] {
  const unplanned = eventsByKind(rows).unplanned;
  const total = valueLostTotal(unplanned);

  const totals = new Map<CauseCategoryId, { dollars: number; eventCount: number; hours: number }>(
    CAUSE_CATEGORIES.map((category) => [category.id, { dollars: 0, eventCount: 0, hours: 0 }]),
  );
  for (const event of unplanned) {
    const bucket = totals.get(event.causeCategory);
    if (!bucket) continue;
    bucket.dollars += valueLostForEvent(event);
    bucket.eventCount += 1;
    bucket.hours += event.hours;
  }

  return [...totals.entries()]
    .map(([causeCategory, bucket]) => ({
      causeCategory,
      label: causeCategoryLabel(causeCategory),
      dollars: bucket.dollars,
      sharePct: total > 0 ? (bucket.dollars / total) * 100 : 0,
      eventCount: bucket.eventCount,
      hours: bucket.hours,
    }))
    .sort(byDollarsThenLabel);
}

/** Cause-filter projection used by the Value lost tab and shared drill-ins. */
export function valueLostRowsForCause(
  rows: readonly DowntimeEvent[],
  selectedCause?: CauseCategoryId,
): readonly DowntimeEvent[] {
  return selectedCause ? rows.filter((event) => event.causeCategory === selectedCause) : rows;
}

/** Cause-filter projection used by the Value lost tab and shared drill-ins. A
 *  cause with no stoppage in the window adds nothing to the ranking and
 *  absence renders nothing, so it is left out here — a cause with events but
 *  US$0 (fully buffered or off the mill path) stays, because its zero is
 *  measured content. The full taxonomy remains in `valueLostByCategory` for
 *  the reads that need every category. The tab ranks an US$ tie (a fully
 *  buffered cause) by the equipment hours it still cost, the same order the
 *  Pareto tab's hours-against-dollars rows take. */
export function valueLostCategoriesForCause(
  rows: readonly DowntimeEvent[],
  selectedCause?: CauseCategoryId,
): CategoryValueRow[] {
  const categories = valueLostByCategory(rows)
    .filter((row) => row.eventCount > 0)
    .slice()
    .sort((a, b) => b.dollars - a.dollars || b.hours - a.hours || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
  return selectedCause ? categories.filter((row) => row.causeCategory === selectedCause) : categories;
}

/** A zero-based chart domain whose upper tick follows the selected window's
 *  cumulative US$K values. `headroomFraction` is the share of the domain that
 *  must stand above the curve's maximum — the chart passes the `clear` label
 *  placement's own ink height plus gap (`markerLabelHeadroomFraction`), so the
 *  top mark's label can sit above its step instead of falling to the plateau
 *  below. The round step is chosen from the required top, so the upper tick is
 *  a multiple of the step the chart then draws (every tick stays a round
 *  number). The fallback keeps empty windows drawable. */
export function cumulativeValueLostYDomain(
  points: readonly number[],
  headroomFraction = 0,
): readonly [number, number] {
  const max = Math.max(0, ...points);
  if (max === 0) return [0, 1];
  const target = headroomFraction > 0 ? max / (1 - Math.min(headroomFraction, 0.9)) : max;
  const roughStep = target / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const leading = roughStep / magnitude;
  const step = (leading <= 1 ? 1 : leading <= 2 ? 2 : leading <= 5 ? 5 : 10) * magnitude;
  return [0, Math.ceil(target / step) * step];
}

/** One point per unplanned event in start order, the running sum of
 *  `valueLostForEvent` in thousands of dollars (`TrendChart` prints raw y
 *  ticks, so the series carries $K and the panel subtitle says so). */
export function cumulativeValueLost(rows: readonly DowntimeEvent[]): CumulativeRead {
  const unplanned = eventsByKind(rows).unplanned
    .slice()
    .sort((a, b) =>
      a.start < b.start ? -1 : a.start > b.start ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    );

  const categories: string[] = [];
  const points: number[] = [];
  let running = 0;
  for (const event of unplanned) {
    running += valueLostForEvent(event);
    categories.push(formatDayMonth(event.start));
    points.push(running / 1000);
  }
  return { categories, points };
}

/** The unplanned events, worst first — the preview has to carry the read, and
 *  a date order buries the two events that are most of the year's money. Ties
 *  fall back to the later start so the order is total and stable. */
export function valueLostEventRows(rows: readonly DowntimeEvent[]): ValueLostEventRow[] {
  return eventsByKind(rows)
    .unplanned.map((event) => ({
      id: event.id,
      itemId: event.itemId,
      start: event.start,
      causeLabel: causeCategoryLabel(event.causeCategory),
      subCause: event.subCause,
      hours: event.hours,
      dollars: valueLostForEvent(event),
    }))
    .sort(
      (a, b) =>
        b.dollars - a.dollars ||
        (a.start < b.start ? 1 : a.start > b.start ? -1 : a.id < b.id ? -1 : 1),
    );
}

const DAY_MINUTES = 24 * 60;

/** Windows of this many days or fewer read on an hourly grid: a week is
 *  short enough that a one-day diagonal reads as a ramp, where the comp's form
 *  is a cliff at the event. Longer windows stay on the daily grid. */
export const CUMULATIVE_HOURLY_MAX_DAYS = 7;

/** The boundary spacing a window's cumulative read uses, and how many
 *  boundaries it spans. A 7-day window is 168 h → an hourly grid with 169
 *  points; anything longer is days + 1. */
function cumulativeGridFor(from: SeriesTime, to: SeriesTime): { grid: CumulativeGrid; stepMinutes: number; steps: number } {
  const minutes = diffMinutes(to, from);
  return minutes <= CUMULATIVE_HOURLY_MAX_DAYS * DAY_MINUTES
    ? { grid: 'hour', stepMinutes: 60, steps: Math.round(minutes / 60) }
    : { grid: 'day', stepMinutes: DAY_MINUTES, steps: Math.round(minutes / DAY_MINUTES) };
}

/** The cumulative read on a calendar axis: one point per boundary from the
 *  window's start to its end, each the running sum of `valueLostForEvent` over
 *  the unplanned events that started before it, in thousands of dollars.
 *  Windows of 7 days or fewer use an hourly grid (`hours + 1` points) so each
 *  event rises within its own hours; longer windows use a daily grid
 *  (`days + 1` points). The total holds flat between events.
 *
 *  Point 0 is the window's start; point i (i ≥ 1) closes the boundary that
 *  ends at it, so it is labelled with the day that ends there —
 *  `from + (i − 1)` steps — and the last point is the window's final day.
 *  The first point is 0 and the last is the window's `valueLostTotal`. */
export function cumulativeValueLostOnGrid(
  rows: readonly DowntimeEvent[],
  from: SeriesTime,
  to: SeriesTime,
): CumulativeWindowRead {
  const { grid, stepMinutes, steps } = cumulativeGridFor(from, to);
  const unplanned = eventsByKind(rows).unplanned;
  const categories: string[] = [];
  const points: number[] = [];
  for (let step = 0; step <= steps; step += 1) {
    const boundary = addMinutes(from, step * stepMinutes);
    const running = unplanned
      .filter((event) => event.start < boundary)
      .reduce((total, event) => total + valueLostForEvent(event), 0);
    categories.push(formatDayMonth(addMinutes(from, Math.max(0, step - 1) * stepMinutes)));
    points.push(running / 1000);
  }
  return { categories, points, grid };
}

export type CumulativeValueLostMarker = {
  /** Index into `cumulativeValueLostOnGrid(rows, from, to).points`: the
   *  boundary that closes the event's start day (or start hour), i.e. the top
   *  of its step. */
  index: number;
  /** The running total at that boundary, US$ thousands. */
  valueK: number;
  itemId: DowntimeItemId;
  start: SeriesTime;
  subCause: string;
  dollars: number;
};

/** The `count` costliest events placed on the top of their step on the window's
 *  cumulative curve, in time order. A mark sits on the boundary that closes the
 *  day (or hour) its event started in. */
export function cumulativeValueLostMarkers(
  rows: readonly DowntimeEvent[],
  from: SeriesTime,
  to: SeriesTime,
  count = 2,
): CumulativeValueLostMarker[] {
  const { stepMinutes } = cumulativeGridFor(from, to);
  const curve = cumulativeValueLostOnGrid(rows, from, to).points;
  return valueLostEventRows(rows)
    .filter((row) => row.dollars > 0)
    .slice(0, count)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0))
    .map((row) => {
      const index = Math.min(curve.length - 1, Math.floor(diffMinutes(row.start, from) / stepMinutes) + 1);
      return {
        index,
        valueK: curve[index]!,
        itemId: row.itemId,
        start: row.start,
        subCause: row.subCause,
        dollars: row.dollars,
      };
    });
}

/** The label the cumulative chart prints beside a mark: the event's own day,
 *  its item and sub-cause, and the dollars it lost. Shared by the tab and the
 *  probe so the chart's label boxes are measured against the drawn string. */
export function cumulativeValueLostMarkerLabel(marker: CumulativeValueLostMarker): string {
  return `${formatDayMonth(marker.start)} · ${marker.itemId} ${marker.subCause.toLowerCase()} · ${formatCurrency(marker.dollars)}`;
}
